// Pure, framework-free core for the admin task calendar/list (Sprint 21 WP-A): status colors,
// task→calendar-event mapping, filter-option derivation and filtering. No React / FullCalendar /
// service imports so it is trivially unit-testable; the dashboard, calendar and filter bar wire it up.
//
// Task shape = the enriched list serializer (nobilis-social PR #36). The relation fields and
// createdByName are OPTIONAL: until #36 is deployed the current serializer omits them, so every
// helper treats them as possibly-absent and degrades gracefully (empty options / no relation chips)
// rather than breaking.

export interface TaskPerson {
  id: number
  firstName?: string
  lastName?: string
  email?: string
}

export interface TaskExperience {
  id: number
  title?: string
}

export interface AdminTask {
  id: number
  title?: string
  notes?: string
  dueDate?: string | null
  status?: string
  statusDisplay?: string
  createdBy?: number | null
  createdByName?: string | null
  relatedMembers?: TaskPerson[]
  relatedPartners?: TaskPerson[]
  relatedExperiences?: TaskExperience[]
}

export type TaskStatus = 'pending' | 'in_progress' | 'resolved' | 'cancelled'

export const TASK_STATUSES: TaskStatus[] = ['pending', 'in_progress', 'resolved', 'cancelled']

// Task colors reflect status, on the established palette (maroon #540008 / near-black #151515 /
// teal #163944 / gold #978B63), with a harmonized green/grey for the two terminal states so all
// four read as distinct.
export const STATUS_COLORS: Record<string, string> = {
  pending: '#978B63', // gold
  in_progress: '#163944', // teal
  resolved: '#3F7D5A', // harmonized green
  cancelled: '#9AA0A6', // muted grey
}

// Fallback to the brand maroon for any unexpected status value.
export const statusColor = (status?: string | null): string =>
  (status && STATUS_COLORS[status]) || '#540008'

export const personName = (p: TaskPerson): string =>
  `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.email || `#${p.id}`

// One `entity` filter covers both members and partners ("member:<id>" / "partner:<id>"), since a
// task can link either and the brief groups them as "member-or-partner".
export interface TaskFilters {
  status: string // '' = all
  staff: string // createdBy id as string; '' = all
  entity: string // 'member:<id>' | 'partner:<id>'; '' = all
  activity: string // related experience id as string; '' = all
}

export const EMPTY_FILTERS: TaskFilters = {status: '', staff: '', entity: '', activity: ''}

export const hasActiveFilters = (f: TaskFilters): boolean =>
  Boolean(f.status || f.staff || f.entity || f.activity)

export interface FilterOption {
  value: string
  label: string
}

export interface FilterOptions {
  staff: FilterOption[]
  entities: FilterOption[]
  activities: FilterOption[]
}

const sortByLabel = (opts: FilterOption[]) =>
  [...opts].sort((a, b) => a.label.localeCompare(b.label))

// Build the dropdown option sets from the DISTINCT values actually present on the loaded tasks, so
// the filters only ever offer choices that match something (and stay empty until #36 exposes the
// relations).
export const deriveFilterOptions = (tasks: AdminTask[]): FilterOptions => {
  const staff = new Map<string, string>()
  const entities = new Map<string, string>()
  const activities = new Map<string, string>()

  for (const t of tasks) {
    if (t.createdBy != null) {
      staff.set(String(t.createdBy), t.createdByName || `#${t.createdBy}`)
    }
    for (const m of t.relatedMembers ?? []) {
      entities.set(`member:${m.id}`, `${personName(m)} · member`)
    }
    for (const p of t.relatedPartners ?? []) {
      entities.set(`partner:${p.id}`, `${personName(p)} · partner`)
    }
    for (const e of t.relatedExperiences ?? []) {
      activities.set(String(e.id), e.title || `#${e.id}`)
    }
  }

  const toOptions = (m: Map<string, string>) =>
    sortByLabel(Array.from(m, ([value, label]) => ({value, label})))

  return {staff: toOptions(staff), entities: toOptions(entities), activities: toOptions(activities)}
}

// Apply every active filter (client-side over the loaded set) so ONE filtered array can drive both
// the list and the calendar identically.
export const applyTaskFilters = (tasks: AdminTask[], f: TaskFilters): AdminTask[] =>
  tasks.filter((t) => {
    if (f.status && t.status !== f.status) return false
    if (f.staff && String(t.createdBy ?? '') !== f.staff) return false
    if (f.activity && !(t.relatedExperiences ?? []).some((e) => String(e.id) === f.activity)) {
      return false
    }
    if (f.entity) {
      const [kind, id] = f.entity.split(':')
      const list = kind === 'partner' ? t.relatedPartners ?? [] : t.relatedMembers ?? []
      if (!list.some((x) => String(x.id) === id)) return false
    }
    return true
  })

export interface CalendarEvent {
  id: string
  title: string
  start: string
  backgroundColor: string
  borderColor: string
  extendedProps: {
    status?: string
    statusDisplay?: string
    staff?: string | null
    members: string[]
    partners: string[]
    activities: string[]
  }
}

// Map a task to a FullCalendar event. Returns null when the task has no due date (nothing to place
// on the calendar) — callers filter those out (and can surface the count).
export const taskToEvent = (t: AdminTask): CalendarEvent | null => {
  if (!t.dueDate) return null
  const color = statusColor(t.status)
  return {
    id: `task-${t.id}`,
    title: t.title || 'Untitled task',
    start: t.dueDate,
    backgroundColor: color,
    borderColor: color,
    extendedProps: {
      status: t.status,
      statusDisplay: t.statusDisplay,
      staff: t.createdByName,
      members: (t.relatedMembers ?? []).map(personName),
      partners: (t.relatedPartners ?? []).map(personName),
      activities: (t.relatedExperiences ?? []).map((e) => e.title || `#${e.id}`),
    },
  }
}

export const tasksToEvents = (tasks: AdminTask[]): CalendarEvent[] =>
  tasks.map(taskToEvent).filter((e): e is CalendarEvent => e !== null)

// The numeric task id behind an event id ('task-42' → 42), for click-to-open.
export const eventIdToTaskId = (eventId: string): number | null => {
  const m = /^task-(\d+)$/.exec(eventId)
  return m ? Number(m[1]) : null
}
